import type { Address } from 'viem';
import type { PendingTx as TrackerPendingTx } from '@/modules/tx-tracker';
import type { Events } from './track';

export type TxKind =
  | 'sell_subscription'
  | 'claim_san'
  | 'claim_points'
  | 'claim_legacy_san'
  | 'withdraw_zk_fees'
  | 'stake_approve'
  | 'stake_deposit'
  | 'stake_withdraw';

export type SellSubscriptionDetails = {
  kind: 'sell_subscription';
  issuerAddress: Address;
  issuerUsername: string;
  inputValue: string;
  iOwnAmount: number;
};

type ClaimSanDetails = { kind: 'claim_san' };
type ClaimPointsDetails = { kind: 'claim_points' };
type ClaimLegacySanDetails = { kind: 'claim_legacy_san' };
type WithdrawZkFeesDetails = { kind: 'withdraw_zk_fees' };
type StakeApproveDetails = {
  kind: 'stake_approve';
  kolAddress: Address;
};
export type StakeDepositDetails = {
  kind: 'stake_deposit';
  kolAddress: Address;
  amount: string;
  priorActiveStake: string;
};
// Withdraws one whole stake deposit by its on-chain id (the contract has no
// partial withdraw). priorActiveStake is the provider's decimal-SAN total
// before the withdraw — the indexer poll waits for it to drop.
// stakeInactive marks a punished stake: withdrawing one never moves
// active_stake_on_user, so the provider polls the withdrawn row dropping out
// of /v1/stakes instead. Optional — older persisted txs lack it and poll
// active_stake_on_user to its timeout.
export type StakeWithdrawDetails = {
  kind: 'stake_withdraw';
  stakeId: number;
  kolAddress: Address;
  amount: string;
  priorActiveStake: string;
  stakeInactive?: boolean | undefined;
};

export type TxDetails =
  | SellSubscriptionDetails
  | ClaimSanDetails
  | ClaimPointsDetails
  | ClaimLegacySanDetails
  | WithdrawZkFeesDetails
  | StakeApproveDetails
  | StakeDepositDetails
  | StakeWithdrawDetails;

export type DetailsFor<K extends TxKind> = Extract<TxDetails, { kind: K }>;

// The analytics event each kind's lifecycle emits (signed / rejected /
// mined / reverted, with `status` merged in).
export const TX_KIND_EVENT = {
  sell_subscription: 'sell_keys',
  claim_san: 'withdraw',
  claim_points: 'withdraw',
  claim_legacy_san: 'withdraw',
  withdraw_zk_fees: 'withdraw',
  stake_approve: 'stake_approve_attempted',
  stake_deposit: 'stake_attempted',
  stake_withdraw: 'unstake_attempted',
} as const satisfies Record<TxKind, keyof Events>;

export type KindEventMap = typeof TX_KIND_EVENT;

export type PendingTx = TrackerPendingTx<keyof Events, TxDetails>;
